import { ref, useContext, useRouter } from "@nuxtjs/composition-api";
import { usePostLogin } from "./useLogin";
import { useOAuthStore } from "./useOAuthStore";
import { loadGisScript } from "./loadGisScript";
import OAUTH_LOGIN_MUTATION from "~/graphql/oauthLogin.graphql";

// Sensitive phone scope. It can only be obtained through the OAuth authorization
// (code) flow, which deliberately shows Google's consent screen. Requested only
// on registration entry points (requestPhone) so a new user's phone number comes
// back on the profile and prefills the form — never on the login path.
const REGISTER_SCOPE =
  "openid email profile https://www.googleapis.com/auth/user.phonenumbers.read";

/**
 * Whether the Google phone-number scope may be requested during registration.
 * Mirrors the backend GOOGLE_FETCH_PHONE_NUMBER switch so the feature can be
 * toggled in one place.
 * @returns {boolean}
 */
export function isPhoneImportEnabled() {
  const flag = process.env.GOOGLE_FETCH_PHONE_NUMBER;
  return flag === true || flag === "true" || flag === "1";
}

/**
 * @param {Object} [options]
 * @param {boolean} [options.navigate=true] - Navigate to the next page after a
 *   successful login. Set false to stay on the current page (e.g. dialog login).
 * @param {boolean} [options.requestPhone=false] - Request the sensitive phone
 *   scope so a registering user's phone number is fetched from Google and
 *   prefilled. Use only on registration entry points: it forces the OAuth code
 *   flow and Google's consent screen, which we keep off the login path. Honored
 *   only when isPhoneImportEnabled() is true.
 * @param {Function} [options.onSuccess] - Called after a successful login.
 */
export function useGoogleLogin({
  navigate = true,
  requestPhone = false,
  onSuccess,
} = {}) {
  const { $mutate, $logger, i18n, localePath } = useContext();
  const router = useRouter();
  const postLogin = usePostLogin();
  const oauthStore = useOAuthStore();

  const loading = ref(false);
  const error = ref(null);

  // Language code for the Google Identity Services loader (`hl`), so the
  // rendered "Sign in with Google" button matches the UI locale.
  const gisLocale = i18n.locale?.split("-")[0];

  // Registration that imports the phone number needs the OAuth authorization
  // (code) flow plus the sensitive phone scope, which legitimately shows
  // Google's consent screen. Plain login authenticates with the ID-token flow
  // (Sign in with Google), which shows only the account picker — no consent
  // screen, hence no second popup.
  const useAuthCode = requestPhone && isPhoneImportEnabled();

  async function completeLogin(token) {
    loading.value = true;
    error.value = null;

    try {
      const resp = await $mutate(OAUTH_LOGIN_MUTATION, {
        provider: "google",
        token,
      });

      if (resp.status === "LOGGED_IN") {
        await postLogin(
          {
            token: resp.token,
            personId: resp.personId,
            isMate: resp.isMate,
          },
          { navigate }
        );
        if (onSuccess) {
          await onSuccess();
        }
      } else if (resp.status === "NEEDS_REGISTRATION") {
        oauthStore.setOAuthData(resp.linkingToken, resp.profile);
        router.push(localePath({ name: "oauthRegisterPage" }));
      } else if (resp.status === "NEEDS_LINKING") {
        oauthStore.setOAuthData(resp.linkingToken, resp.profile);
        oauthStore.setLinkingRequired();
      }
    } catch (e) {
      $logger.error("Google login failed", e);
      error.value =
        typeof e === "string" && e ? e : i18n.t("login.googleLoginError");
    } finally {
      loading.value = false;
    }
  }

  // --- Authorization code flow (registration with phone import) ---

  let codeClient = null;

  function handleCodeResponse(response) {
    if (response.error) {
      $logger.warn("Google login canceled or failed", response);
      if (response.error !== "popup_closed_by_user") {
        error.value = i18n.t("login.googleLoginError");
      }
      return;
    }
    completeLogin(response.code);
  }

  async function ensureCodeClient() {
    if (codeClient) return codeClient;

    const clientId = process.env.GOOGLE_CLIENT_ID;
    if (!clientId) {
      $logger.warn("GOOGLE_CLIENT_ID not configured");
      return null;
    }

    await loadGisScript(gisLocale);

    codeClient = window.google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: REGISTER_SCOPE,
      ux_mode: "popup",
      prompt: "",
      callback: handleCodeResponse,
    });
    return codeClient;
  }

  async function prepare() {
    if (!useAuthCode) return;
    try {
      await ensureCodeClient();
    } catch (e) {
      $logger.error("Failed to initialize Google login", e);
      error.value = i18n.t("login.googleLoginError");
    }
  }

  async function initiateLogin() {
    error.value = null;
    try {
      const client = await ensureCodeClient();
      if (!client) return;
      client.requestCode();
    } catch (e) {
      $logger.error("Failed to initiate Google login", e);
      error.value = i18n.t("login.googleLoginError");
    }
  }

  // --- ID-token flow (login / phone-less registration) ---

  function handleCredentialResponse(response) {
    if (!response?.credential) {
      $logger.warn("Google sign-in returned no credential", response);
      error.value = i18n.t("login.googleLoginError");
      return;
    }
    completeLogin(response.credential);
  }

  async function renderButton(el) {
    if (!el) return;

    const clientId = process.env.GOOGLE_CLIENT_ID;
    if (!clientId) {
      $logger.warn("GOOGLE_CLIENT_ID not configured");
      return;
    }

    try {
      await loadGisScript(gisLocale);

      window.google.accounts.id.initialize({
        client_id: clientId,
        ux_mode: "popup",
        callback: handleCredentialResponse,
      });
      // The button is rendered invisibly and scaled up to cover the styled
      // button beneath it (see GoogleLoginButton.vue), so these cosmetic options
      // are not user-visible; only the fixed width gives the scale a known base.
      window.google.accounts.id.renderButton(el, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "signin_with",
        shape: "rectangular",
        logo_alignment: "center",
        locale: i18n.locale?.replace("-", "_"),
      });
    } catch (e) {
      $logger.error("Failed to render Google sign-in button", e);
      error.value = i18n.t("login.googleLoginError");
    }
  }

  return {
    loading,
    error,
    useAuthCode,
    prepare,
    initiateLogin,
    renderButton,
  };
}
